import { TableHeadSortable } from '@/components/_common/Table';
import TableBody from '@/components/_common/Table/TableBody';
import { Grid, Table as MuiTable, TableProps } from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from '@/_utils/axios';
import { selectAssignedUserFilter } from '@/redux/selectors/dataTableSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';
import TableRow, { TableRowHidden } from './TableRow';
import styled from '@emotion/styled';
import { TableColumn } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import { DragDropProvider } from '@dnd-kit/react';
import { DndKitEvents } from '@/components/Dashboard/Widgets/WidgetFrame';
import { move } from "@dnd-kit/helpers";
import { useToast } from '@/components/_common/Toast';
import { UniqueIdentifier } from '@dnd-kit/core';

// This component ended up being very Tasks specific, so it cannot be reused for any DND table
// and will need some refactoring if it is ever used in a different way

type TableDndProps = TableProps & {
  columns: TableColumn[];
  data: any[];
  onReorderRows: (id: UniqueIdentifier, priority: number | null) => Promise<void>;
  onRowClick?: (e: React.MouseEvent<HTMLTableRowElement>, item: any) => void;
  activeRowId?: number;
  onSortColumn: (column: string) => void;
  scrollable: boolean;
  scrollEnd: 'left' | 'right' | null;
};

export type DroppableType = {
  items: any[];
  showEmptyDropzone: boolean;
  hidden?: boolean;
};

export type Droppables = {
  priority: DroppableType;
  nonPriority: DroppableType;
};

type DragItemSortable = {
  sortable: {
    index: number;
  };
}

const TableDnd = ({
  columns = [],
  data = [],
  onReorderRows,
  onRowClick,
  activeRowId,
  onSortColumn,
  scrollable,
  scrollEnd,
  ...tableProps
}: TableDndProps) => {
  const { success } = useToast();
  const assignedUser = useAppSelector(selectAssignedUserFilter);
  const [hidePriorityItems, setHidePriorityItems] = useState(false);
  const [draggingGroupId, setDraggingGroup] = useState<string | null>(null);
  const [droppables, setDroppables] = useState<Droppables>({
    priority: {
      items: [],
      showEmptyDropzone: true,
    },
    nonPriority: {
      items: data.filter((item) => item.priority == null) || [],
      showEmptyDropzone: true
    }
  });

  useEffect(() => {
    const currentTaskIds = data.map((item) => item.id);

    // get all priority items and add hidden property
    axios
      .post('/core/tasks/search/', {
        has_priority: true,
        order_on: 'priority',
        direction: 'asc',
        show_not_ready: true,
        assigned_users_filter: [assignedUser[0]?.id]
      })
      .then((res) => {
        const items = res.data.data.map((item: any) => ({
          ...item,
          hidden: !currentTaskIds.includes(item.id)
        })) as DroppableType[];
        setDroppables((prev) => ({
          ...prev,
          priority: {
            items,
            showEmptyDropzone: items.length <= 0,
          }
        }));
      });
  }, [data, assignedUser]);

  useEffect(() => {
    if (assignedUser[0]?.id) {
      const nonPrioFromData = data.filter((item) => item.priority == null) || [];
      const allTasksAssignedToMe = !nonPrioFromData.some((item) => !item.assigned_user || item.assigned_user?.id !== assignedUser[0]?.id);
      if (allTasksAssignedToMe) {
        setDroppables((prev) => ({
          ...prev,
          nonPriority: {
            items: nonPrioFromData,
            showEmptyDropzone: nonPrioFromData.length <= 0,
          }
        }));
        if (nonPrioFromData.length <= 0) {
          setHidePriorityItems(false);
        }
      }
    }
  }, [data, assignedUser]);

  const resetPriorityValues = (items: any[]) => items.map((item, index) => ({ ...item, priority: index + 1 }));

  const onDragOver: DndKitEvents["dragover"] = (evt) => {
    const { source, target } = evt.operation;
    if (!source || !target || !source?.id || !target?.id) {
      return;
    }
    evt.preventDefault();
    const sourceGroup = source.data.group;
    const targetGroup = target.data.group;
    const sourceId = source.id;

    if (sourceGroup !== targetGroup) {
      if (targetGroup === 'nonPriority') {
        setDroppables((current) => {
          const extractPriority = { ...current.priority.items.find((items) => items.id === sourceId), priority: 0 };
          const newPriority = current.priority.items.filter((items) => items.id !== sourceId);
          const newNonPriority = [extractPriority, ...current.nonPriority.items];

          return {
            priority: {
              items: resetPriorityValues(newPriority),
              showEmptyDropzone: newPriority.length <= 0,
            },
            nonPriority: {
              items: newNonPriority,
              showEmptyDropzone: newNonPriority.length <= 0,
            },
          }
        });
      } else {
        setDroppables((current) => {
          const extractNonPriority = current.nonPriority.items.find((items) => items.id === sourceId);
          const newNonPriority = current.nonPriority.items.filter((items) => items.id !== sourceId);
          const newPriority = [extractNonPriority, ...current.priority.items];

          return {
            priority: {
              items: resetPriorityValues(newPriority),
              showEmptyDropzone: newPriority.length <= 0,
            },
            nonPriority: {
              items: newNonPriority,
              showEmptyDropzone: newNonPriority.length <= 0,
            },
          }
        });
      }
    } else if (sourceGroup === 'priority') {
      setDroppables((current) => {
        if (current.priority.items.length === 1) {
          return current;
        }

        const newList = move(current.priority.items, evt);
        return {
          ...current,
          priority: {
            items: resetPriorityValues(newList),
            showEmptyDropzone: newList.length <= 0,
          }
        }
      });
    }
  }

  const onDragEnd: DndKitEvents["dragend"] = async (evt) => {
    const { source, target } = evt.operation;
    if (!source || !target || !source?.id || !target?.id) {
      return;
    }

    const sourceId = source.id;
    if (target.data.group !== 'priority') {
      await onReorderRows(sourceId, null);
      success('Task removed from priority list');
    } else {
      await onReorderRows(sourceId, (target as unknown as DragItemSortable).sortable.index + 1);
      if (draggingGroupId !== 'priority') {
        success('Task added to priority list');
      } else {
        success('Task priority changed');
      }
    }

    setDraggingGroup(null);
  }

  const onDragStart: DndKitEvents["dragstart"] = (evt) => {
    if (evt.operation.source ) {
      setHidePriorityItems(false);
      setDraggingGroup(evt.operation.source.data.group as string);
    }
  }

  const onBeforeDragStart: DndKitEvents["beforedragstart"] = (evt) => {
    const target = evt.operation?.activatorEvent?.target as HTMLElement;

    if (target && target.tagName.toLocaleLowerCase() != 'body' && target.id !== 'drag-handle' && !target.closest('#drag-handle')) {
      evt.preventDefault();
    }
  }

  return (
    <DragDropProvider
      onBeforeDragStart={onBeforeDragStart}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
    >
      <StyledTable dragging={+(!!draggingGroupId)} empty={+!!(droppables.priority.items.length === 0)} {...tableProps}>
        <TableHeadSortable columns={columns} onSort={onSortColumn} scrollable={scrollable} scrollEnd={scrollEnd} />
        {Object.keys(droppables).map((_key) => {
          const key = _key as keyof Droppables;
          return (
            <StyledTableBody
              showline={+(key === 'priority' && (droppables[key].items.length > 0 || !!draggingGroupId))}
              hidden={+(key === 'priority' && hidePriorityItems)}
              key={key}
              dragging={+(!!draggingGroupId)}
              priority={+(key === 'priority')}
            >
              {/* to force a droppable area when the priority section is empty */}
              {droppables[key].showEmptyDropzone && (
                <TableRowHidden group={key} hidden={!draggingGroupId}>
                  <td colSpan={columns.length}>
                    <CenterContentWrapper>
                      <Grid container alignItems="center" justifyContent="center">
                        <StyledPolarisIcon icon={key === "priority" ? "priority" : "cancel"} />
                        <div>Drop to {key === 'priority' ? 'set' : 'remove'} priority</div>
                      </Grid>
                    </CenterContentWrapper>
                  </td>
                </TableRowHidden>
              )}

              {droppables[key as keyof Droppables].items.map((item, index) => (
                <TableRow
                  group={key}
                  onRowClick={onRowClick ? (e) => onRowClick(e, item) : undefined}
                  columns={columns}
                  item={item}
                  index={index}
                  key={`row=${key}-${item.id}-${index}`}
                  isActiveRow={item.id === activeRowId}
                  scrollable={scrollable}
                  scrollEnd={scrollEnd}
                  draggingGroup={draggingGroupId}
                />
              ))}
              {key === 'priority' && (droppables[key].items.length > 0 || !!draggingGroupId) ? (
                <tr className="row-hidden">
                  <td colSpan={columns.length}>
                    <CenterContentWrapper>
                      <PriorityToggle hidden={hidePriorityItems} onClick={() => setHidePriorityItems(!hidePriorityItems)}>
                        <span>⚡︎ Priority</span>
                        <UpDownIcon rotate={90} fontSize="1rem" icon={hidePriorityItems ? 'up' : 'down'} />
                      </PriorityToggle>
                    </CenterContentWrapper>
                  </td>
                </tr>
              ) : null}
            </StyledTableBody>
          );
        })}
      </StyledTable>
    </DragDropProvider>
  );
};

export default TableDnd;

const StyledPolarisIcon = styled(PolarisIcon)`
  width: 30px;
  min-height: 0;
  color: ${({ theme }) => theme.palette.warning.light};
  margin-right: ${({ theme }) => theme.spacing(1)};
  transition: ease all 0.5s;
  animation: pulse2 1.25s ease infinite alternate;
`;

const StyledTableBody = styled(TableBody) <{ dragging: number; priority: number; hidden: number; showline: number; }>`
  ${({ priority }) =>
    priority ? `
      &:before {
        content: "";
        height: 5px;
        display: block;
      }
    ` : null}

  ${props => props.hidden ? `
    overflow: visible;
    tr:not(.row-hidden) {
      display: none;
    }
  ` : null};

  ${props => props.showline ? `
    position: relative;

    &::after {
      content: " ";
      width: 100%;
      height: 2px;
      background: ${props.theme?.palette?.primary?.main};
      display: block;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
    }
  `: null};

  ${({ theme, dragging }) =>
    dragging &&
    `
    &.priority .emptyDropZone td {
      background: ${theme.palette.primary.main}5a;
      transition: ease .2s background-color;
    }
  `}

`;

const CenterContentWrapper = styled.div`
  position: relative;
  max-width: 100vw;
  width: 100%;
`;

const PriorityToggle = styled.div<{ hidden?: boolean; }>`
  cursor: pointer;
  text-align: center;
  font-size: 0.75rem;
  width: 80px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme?.palette?.primary?.main};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  color: #000;
  position: absolute;
  bottom: -14px;
  z-index: 2;
  left: 0;
  right: 0;
  margin: 0 auto;
`;

const UpDownIcon = styled(PolarisIcon)`
  margin-left: ${props => props.theme.spacing(0.5)};
`;

const StyledTable = styled(MuiTable) <{ dragging: number; empty: number }>`
  ${({ theme, dragging }) =>
    dragging &&
    `
    & .priority {
      transition: ease box-shadow .5s;
      box-shadow: 0 0.25px 0 2px ${theme.palette.primary.main}99;
      opacity: 1;
    }
    & .nonPriority tr {
      pointer-events: 'none';
      opacity: 0.5;
    }
    & .emptyDropZone {
      transition: all .5s ease;
      opacity: 1;
      & + tr[data-rbd-placeholder-context-id] {
        display: none !important;
      }
    }
    & .emptyDropZone td {
      opacity: 1;
      font-size: 1.2rem;
      max-height: none;
      line-height: 1.2rem;
      transition: all .5s ease;
      & .emptyIcon {
        transition: ease all .5s;
        min-height: 30px;
      }
      & > div {
        transition: ease all .5s;
        padding: ${theme.spacing(3)};
      }
    }
  `}

  ${({ empty }) =>
    empty &&
    `
  & .emptyPriority {
    & .priority {
      opacity: 0;
      transition: ease opacity .5s;
    }

    &.isDragging .priority {
      transition: ease opacity .5s;
      opacity: 1;
    }
  }

  `}
`;